import { useEffect, useRef, useCallback, useState } from 'react'
import { ChatMessage, ChatMessagePage } from '../types/chat'
import { ChatService } from '../services/chat.service'

interface UseChatOptions {
  roomId: string | null
  enabled?: boolean
  pollingInterval?: number // in milliseconds
}

interface UseChatReturn {
  messages: ChatMessage[]
  loading: boolean
  error: string | null
  hasMore: boolean
  sendMessage: (content: string) => Promise<void>
  loadMore: () => Promise<void>
  searchMessages: (query: string) => Promise<ChatMessage[]>
  editMessage: (messageId: string, content: string) => Promise<void>
  deleteMessage: (messageId: string) => Promise<void>
}

// Cache for author names to avoid repeated lookups
const authorCache = new Map<string, string>()

export function useChat({ roomId, enabled = true, pollingInterval = 1000 }: UseChatOptions): UseChatReturn {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasMore, setHasMore] = useState(false)
  const loadingRef = useRef(false)
  const pollingRef = useRef<NodeJS.Timeout | null>(null)
  const isVisibleRef = useRef(true)
  const lastMessageIdRef = useRef<string | null>(null)

  // Load initial messages
  const loadMessages = useCallback(async () => {
    if (!roomId || loadingRef.current) return

    loadingRef.current = true
    setLoading(true)
    setError(null)

    try {
      const result: ChatMessagePage = await ChatService.getMessages(roomId, { limit: 50 })
      setMessages(result.messages)
      setHasMore(result.hasMore)

      // Populate author cache
      result.messages.forEach((m) => {
        if (m.author_name) {
          authorCache.set(m.user_id, m.author_name)
        }
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load messages')
    } finally {
      setLoading(false)
      loadingRef.current = false
    }
  }, [roomId])

  // Load more (older) messages
  const loadMore = useCallback(async () => {
    if (!roomId || !hasMore || loadingRef.current || messages.length === 0) return

    loadingRef.current = true

    try {
      const oldestMessage = messages[0]
      const result = await ChatService.getMessages(roomId, {
        limit: 50,
        before: oldestMessage.created_at,
      })

      setMessages((prev) => [...result.messages, ...prev])
      setHasMore(result.hasMore)

      // Populate author cache
      result.messages.forEach((m) => {
        if (m.author_name) {
          authorCache.set(m.user_id, m.author_name)
        }
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load more messages')
    } finally {
      loadingRef.current = false
    }
  }, [roomId, hasMore, messages])

  // Poll for new messages
  const pollMessages = useCallback(async () => {
    if (!roomId || !isVisibleRef.current) return

    try {
      const result: ChatMessagePage = await ChatService.getMessages(roomId, { limit: 50 })

      // Check if there are new messages by comparing the latest message ID
      const latestMessage = result.messages[result.messages.length - 1]
      if (latestMessage && latestMessage.id !== lastMessageIdRef.current) {
        setMessages(result.messages)
        setHasMore(result.hasMore)
        lastMessageIdRef.current = latestMessage.id

        // Populate author cache
        result.messages.forEach((m) => {
          if (m.author_name) {
            authorCache.set(m.user_id, m.author_name)
          }
        })
      }
    } catch (err) {
      // Silent fail for polling - don't spam errors
      console.error('[Chat] Polling error:', err)
    }
  }, [roomId])

  // Send a message
  const sendMessage = useCallback(async (content: string) => {
    if (!roomId) throw new Error('No room selected')

    await ChatService.sendMessage({ room_id: roomId, content })
    // Message will appear via polling (within 1 second)
    // Optionally trigger immediate poll for faster feedback
    pollMessages()
  }, [roomId, pollMessages])

  // Search messages
  const searchMessages = useCallback(async (query: string): Promise<ChatMessage[]> => {
    if (!roomId) return []
    return ChatService.searchMessages(roomId, query)
  }, [roomId])

  // Edit a message
  const editMessage = useCallback(async (messageId: string, content: string) => {
    const updated = await ChatService.editMessage(messageId, content)
    // Immediately update local state for instant feedback
    setMessages((prev) =>
      prev.map((m) => (m.id === messageId ? { ...m, content: updated.content, updated_at: updated.updated_at } : m))
    )
  }, [])

  // Delete a message
  const deleteMessage = useCallback(async (messageId: string) => {
    await ChatService.deleteMessage(messageId)
    // Deletion will be reflected via polling
    // Immediately remove from local state for instant feedback
    setMessages((prev) => prev.filter((m) => m.id !== messageId))
  }, [])

  // Set up polling
  useEffect(() => {
    if (!roomId || !enabled) return

    // Load initial messages
    loadMessages().then(() => {
      // Set the initial last message ID after loading
      setMessages((prev) => {
        if (prev.length > 0) {
          lastMessageIdRef.current = prev[prev.length - 1].id
        }
        return prev
      })
    })

    // Handle visibility change - pause polling when tab is hidden
    const handleVisibilityChange = () => {
      isVisibleRef.current = !document.hidden
      if (!document.hidden) {
        // Immediately poll when tab becomes visible again
        pollMessages()
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)

    // Start polling
    pollingRef.current = setInterval(pollMessages, pollingInterval)
    console.log('[Chat] Polling started with interval:', pollingInterval, 'ms')

    // Cleanup on unmount or roomId change
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current)
        pollingRef.current = null
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      console.log('[Chat] Polling stopped')
    }
  }, [roomId, enabled, loadMessages, pollMessages, pollingInterval])

  return {
    messages,
    loading,
    error,
    hasMore,
    sendMessage,
    loadMore,
    searchMessages,
    editMessage,
    deleteMessage,
  }
}

